import React from 'react';
import { makeObservable, observable, computed, action, reaction, when } from 'mobx';
import find from 'lodash/find';
import get from 'lodash/get';

import { getStructure, getOptions, getData } from 'data/wp';
import joinPath from 'utilities/joinPath';
import ShortlistStore from './ShortlistStore';


const invalidTemplates = [ 'elementor_canvas', 'elementor_header_footer' ];

const APP_SETTINGS_STORAGE_KEY = 'salesApp_appSettings';

const defaultAppSettings = {
    hideEmailShortlistButton: false,
    syncMode: 'standalone', // 'standalone' | 'master' | 'servant'
};


class DataStore {
    structure = null;
    settings = null;
    data = null;
    currentDevelopment = null;
    appSettings = { ...defaultAppSettings };

    shortlistStore = new ShortlistStore();
    
    constructor() {
        makeObservable(this, {
            structure: observable,
            data: observable,
            settings: observable,
            currentDevelopment: observable,
            appSettings: observable,
            shortlistStore: observable,
            
            loaded: computed,
            appPages: computed,
            
            updateStructure: action,
            updateSettings: action,
            updateData: action,
            updateDevelopment: action,
            updateAppSetting: action,
        });
        
        this.loadAppSettingsFromLocalStorage();
        
        reaction(
            () => this.appSettings,
            (appSettings) => {
                try {
                    localStorage.setItem(APP_SETTINGS_STORAGE_KEY, JSON.stringify(appSettings));
                } catch (error) {
                    console.error('Failed to save app settings to localStorage:', error);
                }
            }
        );
        
        this.fetchStructure();
        
        reaction(
            () => ({
                structure: this.structure,
                currentDevelopment: this.currentDevelopment,
            }),
            () => {
                this.fetchData();
            }
        );
        
        when(
            () => !!this.structure,
            () => this.fetchSettings()
        );
    }
    
    get loaded() {
        return !!(this.structure && this.data && this.settings);
    }
    
    get appPages() {
        return this.data.pages.filter(({ template }) => !invalidTemplates.includes(template));
    }
    
    
    // DATA FETCHING //////////////////////////////////////////////////////////
    
    fetchStructure() {
        getStructure().then((structure) => this.updateStructure(structure));
    }
    
    fetchSettings() {
        if (this.structure && this.structure.settings) {
            Promise.all(this.structure.settings.map((name) => getOptions(name))).then((settingsData) => {
                const settings = {};
                
                this.structure.settings.forEach((name, index) => {
                    settings[name] = settingsData[index].acf;
                });
                
                this.updateSettings(settings);
            });
        }
    }
    
    fetchData() {
        if (this.structure && this.structure.data) {
            Promise.all(this.structure.data.map((dataSpec) => getData(dataSpec, this.currentDevelopment))).then((postData) => {
                const newData = {};
                
                this.structure.data.forEach(({ name }, index) => {
                    newData[name] = postData[index];
                });
                
                this.updateData(newData);
            });
        }
    }
    
    
    // DATA UPDATING //////////////////////////////////////////////////////////
    
    updateStructure(structure) {
        if (structure.multi_development && structure.developments) {
            this.currentDevelopment = structure.developments[0].id;
            console.log('updated currentDevelopment:', this.currentDevelopment); //eslint-disable-line
        }
        
        console.log('updated structure:', structure); //eslint-disable-line
        this.structure = structure;
    }
    
    updateSettings(settings) {
        console.log('updated settings:', settings); //eslint-disable-line
        this.settings = settings;
    }
    
    updateData(data) {
        console.log('updated data:', data); //eslint-disable-line
        this.data = data;
    }
    
    updateDevelopment(currentDevelopment) {
        console.log('updated currentDevelopment:', currentDevelopment); //eslint-disable-line
        this.currentDevelopment = currentDevelopment;
    }
    
    updateAppSetting(key, value) {
        this.appSettings = { ...this.appSettings, [key]: value };
    }
    
    loadAppSettingsFromLocalStorage() {
        try {
            const saved = localStorage.getItem(APP_SETTINGS_STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                this.appSettings = { ...defaultAppSettings, ...parsed };
            }
        } catch (error) {
            console.error('Failed to load app settings from localStorage:', error);
        }
    }
    
    
    // DATA GETTERS ///////////////////////////////////////////////////////////
    
    getItem(type, field, value) {
        return find(this.data[type], [ field, value ]);
    }

    isUnitTypeAvailable(unitTypeID) {
        return this.data.units.some((unit) => unit.acf.unit_type === unitTypeID && unit.acf.status === 'available');
    }
    
    getSetting(type, field) {
        return get(this.settings, `${type}.${field}`, null);
    }

    getUnitPath(unit) {
        if (!this.data || !unit?.acf?.level) return null;
        const level = this.getItem('levels', 'id', unit.acf.level);
        if (!level) return null;
        const apartmentsPage = this.appPages?.find(p => p.template === 'apartments');
        const apartmentsBasePath = apartmentsPage ? (apartmentsPage.slug === 'home' ? '/' : `/${apartmentsPage.slug}`) : '/apartments';
        return joinPath(apartmentsBasePath, level.slug, unit.slug);
    }
}


export const DataStoreContext = React.createContext(new DataStore());

export const useDataStore = () => React.useContext(DataStoreContext);

